
import * as THREE from "../../../libs/three.js/build/three.module.js";

// Feature C (PointView spec, Sprint 3) - the laser's own plain data model
// (origin, axis basis, per-leg results) and serialization. Raycasting
// against IFC/OBJ meshes and the point-cloud neighbourhood search both
// need live viewer/scene state, so they stay in index.html (see
// computeLaserLegs there) - this module is deliberately just the data
// shape, the same split SectionPlanes.js uses for Feature A.

export const LASER_LEG_KEYS = ["x+", "x-", "y+", "y-", "z+", "z-"];

function legAxisAndSign(key){
	return {axis: key[0], sign: key[1] === "+" ? 1 : -1};
}

let laserIdSeq = 0;

export class ThreeAxisLaser{

	constructor(origin, axisMode, basis, name){
		this.id = `laser_${++laserIdSeq}`;
		this.name = name || `Laser ${laserIdSeq}`;
		this.origin = origin.clone();
		this.axisMode = axisMode; // "global" | "face" | "section"
		this.basis = basis; // {x: THREE.Vector3, y: THREE.Vector3, z: THREE.Vector3} - all unit, right-handed
		this.maxDistance = 20; // metres - search cap per leg
		this.allowContext = false; // "Tillad måling på kontekst" - include ghosted/hidden geometry
		this.legs = {}; // key -> {distance, point: Vector3, source: "ifc"|"cloud"} | null (no hit)
	}

	getDirection(key){
		let {axis, sign} = legAxisAndSign(key);
		return this.basis[axis].clone().multiplyScalar(sign);
	}

	// Combined free width/height along one axis - only meaningful when
	// *both* directions actually hit something (spec: "Hvis begge
	// retninger rammer, vises også samlet fri bredde/højde langs aksen").
	getAxisSpan(axis){
		let pos = this.legs[`${axis}+`];
		let neg = this.legs[`${axis}-`];
		if(!pos || !neg){
			return null;
		}
		return pos.distance + neg.distance;
	}

	moveTo(newOrigin){
		this.origin.copy(newOrigin);
	}

	serialize(){
		let legs = {};
		for(let key of LASER_LEG_KEYS){
			let leg = this.legs[key];
			legs[key] = leg ? {distance: leg.distance, point: [leg.point.x, leg.point.y, leg.point.z], source: leg.source} : null;
		}
		return {
			id: this.id,
			name: this.name,
			origin: [this.origin.x, this.origin.y, this.origin.z],
			axisMode: this.axisMode,
			basis: {
				x: [this.basis.x.x, this.basis.x.y, this.basis.x.z],
				y: [this.basis.y.x, this.basis.y.y, this.basis.y.z],
				z: [this.basis.z.x, this.basis.z.y, this.basis.z.z],
			},
			maxDistance: this.maxDistance,
			allowContext: this.allowContext,
			legs,
		};
	}

	static restore(data){
		let laser = new ThreeAxisLaser(
			new THREE.Vector3(data.origin[0], data.origin[1], data.origin[2]),
			data.axisMode,
			{
				x: new THREE.Vector3(data.basis.x[0], data.basis.x[1], data.basis.x[2]),
				y: new THREE.Vector3(data.basis.y[0], data.basis.y[1], data.basis.y[2]),
				z: new THREE.Vector3(data.basis.z[0], data.basis.z[1], data.basis.z[2]),
			},
			data.name,
		);
		laser.id = data.id || laser.id;
		laser.maxDistance = typeof data.maxDistance === "number" ? data.maxDistance : 20;
		laser.allowContext = !!data.allowContext;
		for(let [key, leg] of Object.entries(data.legs || {})){
			laser.legs[key] = leg ? {distance: leg.distance, point: new THREE.Vector3(leg.point[0], leg.point[1], leg.point[2]), source: leg.source} : null;
		}
		return laser;
	}
}

// Global axis basis - øst/nord/op, this app's world convention throughout
// (X=east, Y=north, Z=up, per every other module's own georeferencing).
export function globalLaserBasis(){
	return {
		x: new THREE.Vector3(1, 0, 0),
		y: new THREE.Vector3(0, 1, 0),
		z: new THREE.Vector3(0, 0, 1),
	};
}

// "Flade" mode - Z follows the clicked face's normal, X/Y constructed in
// the tangent plane. Reuses the exact same up-projection rule Feature B's
// 2D section view uses for its own in-plane "up" (project global Z into
// the plane, falling back to global north for a near-horizontal face) -
// the two features want the same intuitive orientation for the same
// underlying reason.
export function faceLaserBasis(normal){
	let z = normal.clone().normalize();
	let up = new THREE.Vector3(0, 0, 1).addScaledVector(z, -z.z);
	if(up.lengthSq() < 0.0025){
		up = new THREE.Vector3(0, 1, 0).addScaledVector(z, -z.y);
	}
	up.normalize();
	let x = new THREE.Vector3().crossVectors(up, z).normalize();
	return {x, y: up, z};
}

// "Aktivt snit" mode - the 2D section view's own basis, directly:
// right/up/normal become X/Y/Z.
export function sectionLaserBasis(sectionBasis){
	return {x: sectionBasis.right.clone(), y: sectionBasis.up.clone(), z: sectionBasis.normal.clone()};
}
